'use client';
import type { NavigationItem } from '@dealerportal/common';
import { useTranslations } from '@dealerportal/i18n/client';
import {
  getNavParentAsChild,
  isFalsyString,
  isTruthyString,
} from '@dealerportal/utilities';
import classNames from 'classnames';
import { createContext, useEffect, useMemo, useState } from 'react';
import { BetterButton } from './BetterButton';
import NavDesktopMenuItems from './NavDesktopMenuItems';
import NavMobileDropDown from './NavMobileDropDown';
import { NavMobileMenuItemLink } from './NavMobileMenuItemLink';
import NavMobileMenuItems from './NavMobileMenuItems';

type Props = {
  isMobileDevice: boolean;
  menuItems: NavigationItem[];
  className?: string;
  openbuttonclass?: string;
  closebuttonclass?: string;
  headerclass?: string;
  navclass?: string;
  listclass?: string;
  itemclass?: string;
  linkclass?: string;
  childnavclass?: string;
  childlistclass?: string;
  childitemclass?: string;
  childlinkclass?: string;
  grandchildnavclass?: string;
  grandchildlistclass?: string;
  grandchilditemclass?: string;
  grandchildlinkclass?: string;
  greatgrandchildnavclass?: string;
  greatgrandchildlistclass?: string;
  greatgrandchilditemclass?: string;
  greatgrandchildlinkclass?: string;
  showmobile?: string;
  entrypoint?: string;
  mobilemenulabel?: string;
  rtl?: string;
};

export const NavigationContext = createContext({
  className: '',
  openButtonClass: '',
  closeButtonClass: '',
  headerClass: '',
  navclass: '',
  listclass: '',
  itemclass: '',
  linkclass: '',
  childnavclass: '',
  childlistclass: '',
  childitemclass: '',
  childlinkclass: '',
  grandchildnavclass: '',
  grandchildlistclass: '',
  grandchilditemclass: '',
  grandchildlinkclass: '',
  greatgrandchildnavclass: '',
  greatgrandchildlistclass: '',
  greatgrandchilditemclass: '',
  greatgrandchildlinkclass: '',
});

export function Navigation(props: Props) {
  const t = useTranslations('ui');

  const navClassNames = {
    className: props.className || '',
    openButtonClass: props.openbuttonclass || '',
    closeButtonClass: props.closebuttonclass || '',
    headerClass: props.headerclass || '',
    navclass: props.navclass || '',
    listclass: props.listclass || '',
    itemclass: props.itemclass || '',
    linkclass: props.linkclass || '',
    childnavclass: props.childnavclass || '',
    childlistclass: props.childlistclass || '',
    childitemclass: props.childitemclass || '',
    childlinkclass: props.childlinkclass || '',
    grandchildnavclass: props.grandchildnavclass || '',
    grandchildlistclass: props.grandchildlistclass || '',
    grandchilditemclass: props.grandchilditemclass || '',
    grandchildlinkclass: props.grandchildlinkclass || '',
    greatgrandchildnavclass: props.greatgrandchildnavclass || '',
    greatgrandchildlistclass: props.greatgrandchildlistclass || '',
    greatgrandchilditemclass: props.greatgrandchilditemclass || '',
    greatgrandchildlinkclass: props.greatgrandchildlinkclass || '',
  };

  const [navBarOpen, setNavBarOpen] = useState(false);
  const [filteredMenu, setFilterMenu] = useState<NavigationItem | null>(null);
  const [menuName, setMenuName] = useState('Menu');
  const isMobile = props.isMobileDevice || isTruthyString(props.showmobile);

  const handleClick = (clickedItem: NavigationItem) =>
    setFilterMenu((prev) => {
      const navParentItem = getNavParentAsChild({
        item: clickedItem,
        mode: isMobile ? 'mobile' : 'desktop',
      });
      return {
        ...clickedItem,
        children: clickedItem?.children?.length
          ? [navParentItem satisfies NavigationItem, ...clickedItem.children]
          : [],
      };
    });

  const handleBackClick = () => setFilterMenu(null);

  useEffect(() => {
    if (!filteredMenu || !filteredMenu.children?.length) {
      setMenuName('Menu');
    } else {
      setMenuName(filteredMenu.name ?? '');
    }
  }, [filteredMenu, setFilterMenu]);

  const menuItems: NavigationItem[] = useMemo(() => {
    if (!Number(props.entrypoint)) return props.menuItems;

    const menuFromEntryPoint = props.menuItems.find(
      ({ position, hasVisibleChildren }) =>
        position === Number(props.entrypoint) && hasVisibleChildren
    );

    if (menuFromEntryPoint)
      return menuFromEntryPoint?.children ?? ([] as NavigationItem[]);

    return props.menuItems;
  }, [props.entrypoint, props.menuItems]);

  return (
    <NavigationContext.Provider value={navClassNames}>
      <nav
        className={`nav ${navClassNames.className} ${navClassNames.navclass}`}
      >
        {isMobile ? (
          <>
            <BetterButton
              className={`nav__open-button ${navClassNames.openButtonClass}`}
              btnStyle="simple"
              icon='<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M0 80c0-8.8 7.2-16 16-16H432c8.8 0 16 7.2 16 16s-7.2 16-16 16H16C7.2 96 0 88.8 0 80zM0 240c0-8.8 7.2-16 16-16H432c8.8 0 16 7.2 16 16s-7.2 16-16 16H16c-8.8 0-16-7.2-16-16zM448 400c0 8.8-7.2 16-16 16H16c-8.8 0-16-7.2-16-16s7.2-16 16-16H432c8.8 0 16 7.2 16 16z"/></svg>'
              iconSize="6"
              label={props.mobilemenulabel}
              attributes={{
                onClick: () => setNavBarOpen(!navBarOpen),
              }}
            />

            <ul
              className={classNames(
                `nav__list --mobile ui-fixed ui-top-0 ui-h-full ui-min-h-screen ui-w-full ui-z-[9999] !ui-m-0 !ui-pb-8 ui-overflow-y-auto ui-overscroll-contain ui-bg-white !ui-list-none ui-transition-transform ${navClassNames.listclass}`,
                { 'ui-left-0': isFalsyString(props.rtl) },
                { 'ui-right-0': isTruthyString(props.rtl) },
                { 'ui-translate-x-0': navBarOpen },
                {
                  '-ui-translate-x-full':
                    !navBarOpen && isFalsyString(props.rtl),
                },
                {
                  'ui-translate-x-full':
                    !navBarOpen && isTruthyString(props.rtl),
                }
              )}
            >
              <div
                className={classNames(
                  `nav__header ui-flex ui-items-center ui-gap-3 ui-mb-4 ui-px-4 ui-py-2 ui-bg-gray-100 ui-border-solid ui-border-0 ui-border-b ui-border-gray-300 ${navClassNames.headerClass}`,
                  {
                    'ui-justify-between': menuName === 'Menu',
                  }
                )}
                onClickCapture={() => {
                  if (menuName === 'Menu') setNavBarOpen(false);
                  if (menuName !== 'Menu') handleBackClick();
                }}
              >
                <span
                  className={classNames('ui-font-semibold', {
                    'ui-order-1': menuName !== 'Menu',
                  })}
                >
                  {menuName}
                </span>

                <BetterButton
                  btnStyle="simple"
                  className={`nav__close-button ui-p-2 -ui-mr-2 ${navClassNames.closeButtonClass}`}
                  icon={
                    menuName === 'Menu'
                      ? '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z" /></svg>'
                      : '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M48 256a208 208 0 1 1 416 0A208 208 0 1 1 48 256zm464 0A256 256 0 1 0 0 256a256 256 0 1 0 512 0zM271 135L167 239c-9.4 9.4-9.4 24.6 0 33.9L271 377c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-87-87 87-87c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0z"/></svg>'
                  }
                  iconSize="5"
                  attributes={{
                    type: 'button',
                    title: t('closeMenu'),
                  }}
                />
              </div>

              {filteredMenu == null &&
                menuItems?.length &&
                menuItems
                  .filter(
                    ({ hideInMenu, isVisible }) => !hideInMenu && isVisible
                  )
                  .map((menu) => {
                    return menu.children?.length && menu.hasVisibleChildren ? (
                      <NavMobileDropDown
                        menu={menu}
                        key={menu.name}
                        depthLevel={0}
                        onClickHandler={handleClick}
                      />
                    ) : (
                      <NavMobileMenuItemLink
                        key={menu.name}
                        menu={menu}
                        depthLevel={0}
                      />
                    );
                  })}

              {filteredMenu?.children && (
                <NavMobileMenuItems menu={filteredMenu} depthLevel={2} />
              )}
            </ul>
          </>
        ) : (
          <ul
            className={`nav__list --desktop ui-flex ui-flex-wrap ui-justify-center ui-items-center ui-p-0 !ui-m-0 !ui-list-none ${navClassNames.className} ${navClassNames.listclass}`}
          >
            {menuItems.map((menu, index) => {
              const depthLevel = 0;

              if (!menu.hideInMenu) {
                return (
                  <NavDesktopMenuItems
                    key={menu.alias}
                    items={menu}
                    depthLevel={depthLevel}
                    className={props.className}
                  />
                );
              }

              return null;
            })}
          </ul>
        )}
      </nav>
    </NavigationContext.Provider>
  );
}

export default Navigation;
